import type React from "react";
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ChevronDown, ChevronRight } from "lucide-react";
import { client as clientSchemas, type insights } from "@duress/shared";
import { fmtARR, fmtInt, fmtPct } from "@/lib/format";
import { cn } from "@/lib/utils";
import { industryColor } from "./industry-colors";
import { COLUMNS, MORE_STATS } from "./insights-config";
import { useIndustries } from "./useInsights";
import { Select } from "@/components/ui/select";
import { Tooltip } from "@/components/ui/tooltip";
import { ErrorCard } from "@/components/ErrorCard";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { readableTextColor } from "@/lib/colors";
import { useTheme } from "@/lib/theme";

type MetricKey = insights.MetricKey;

function pickMetric(
  bucket: insights.IndustryRow["metrics"],
  key: MetricKey,
): insights.MetricStats | null {
  switch (key) {
    case "extended_rate":
      return bucket.extended_rate;
    case "network_adoption":
      return bucket.network_adoption;
    case "productive_hours":
      return bucket.productive_hours;
    case "sessions":
      return bucket.sessions;
    case "emergency_activations":
      return bucket.emergency_activations;
    case "checkin_rate":
      return bucket.checkin_rate;
  }
}

function formatMonthLabel(value: string): string {
  const [year, month] = value.split("-");
  const m = parseInt(month ?? "0", 10);
  const months = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[m] ?? ""} ${year ?? ""}`;
}

export function IndustryInsightsPage(): React.JSX.Element {
  // Period and view live in the URL so the page you're looking at survives a
  // refresh and can be shared as a link (see UI-08).
  const [searchParams, setSearchParams] = useSearchParams();

  const month = searchParams.get("month");
  const setMonth = (value: string | null): void => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set("month", value);
        else next.delete("month");
        return next;
      },
      { replace: true },
    );
  };

  const view: "cards" | "table" = searchParams.get("view") === "table" ? "table" : "cards";
  const setView = (value: "cards" | "table"): void => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === "table") next.set("view", value);
        else next.delete("view");
        return next;
      },
      { replace: true },
    );
  };

  // Defaults to AU -- the vast majority of clients -- so a UK/NZ client never
  // silently shows up mixed into the benchmark someone is already looking at.
  const regionParam = searchParams.get("region");
  const region: clientSchemas.Region = clientSchemas.REGIONS.includes(
    regionParam as clientSchemas.Region,
  )
    ? (regionParam as clientSchemas.Region)
    : "AU";
  const setRegion = (value: clientSchemas.Region): void => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === "AU") next.delete("region");
        else next.set("region", value);
        return next;
      },
      { replace: true },
    );
  };

  const { data, isLoading, error, refetch } = useIndustries(month, region);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <LoadingSkeleton variant="kpis" />
        <LoadingSkeleton variant="table" />
      </div>
    );
  }
  if (error) {
    return <ErrorCard message="Couldn't load industry insights." onRetry={refetch} />;
  }
  if (!data || data.industries.length === 0) {
    return <EmptyState region={region} onRegionChange={setRegion} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-head font-semibold tracking-tight">Industry insights</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Benchmarks across {data.industries.length}{" "}
            {data.industries.length === 1 ? "industry" : "industries"}
            {data.untaggedClientCount > 0
              ? ` — ${data.untaggedClientCount} untagged client${data.untaggedClientCount === 1 ? "" : "s"} not shown`
              : ""}
            .
            {data.deviceCountAsOfMonth ? (
              <> Device counts as of {formatMonthLabel(data.deviceCountAsOfMonth)}.</>
            ) : null}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <RegionSelector region={region} onChange={setRegion} />
          <PeriodSelector
            month={month}
            availableMonths={data.availableMonths}
            onChange={setMonth}
          />
          <ViewToggle value={view} onChange={setView} />
        </div>
      </div>

      <IndustrySummaryStrip rows={data.industries} />

      {view === "cards" ? (
        <IndustryCards rows={data.industries} />
      ) : (
        <IndustryTable rows={data.industries} />
      )}
    </div>
  );
}

function EmptyState({
  region,
  onRegionChange,
}: {
  region: clientSchemas.Region;
  onRegionChange: (r: clientSchemas.Region) => void;
}): React.JSX.Element {
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h1 className="text-3xl font-head font-semibold tracking-tight">Industry insights</h1>
        <RegionSelector region={region} onChange={onRegionChange} />
      </div>
      <div className="card-duress p-12 text-center">
        <p className="text-sm text-muted-foreground">
          No {region} industry data yet — add industry tags to {region} clients to start seeing
          benchmarks here.
        </p>
      </div>
    </div>
  );
}

function RegionSelector({
  region,
  onChange,
}: {
  region: clientSchemas.Region;
  onChange: (r: clientSchemas.Region) => void;
}): React.JSX.Element {
  return (
    <label className="flex items-center gap-2 text-sm">
      <span className="section-label">Region</span>
      <Select
        value={region}
        onChange={(e) => {
          const value = e.currentTarget.value;
          if (clientSchemas.REGIONS.includes(value as clientSchemas.Region)) {
            onChange(value as clientSchemas.Region);
          }
        }}
      >
        {clientSchemas.REGIONS.map((r) => (
          <option key={r} value={r}>
            {r}
          </option>
        ))}
      </Select>
    </label>
  );
}

function PeriodSelector({
  month,
  availableMonths,
  onChange,
}: {
  month: string | null;
  availableMonths: string[];
  onChange: (m: string | null) => void;
}): React.JSX.Element {
  return (
    <label className="flex items-center gap-2 text-sm">
      <span className="section-label">Period</span>
      <Select
        value={month ?? "all"}
        onChange={(e) => onChange(e.currentTarget.value === "all" ? null : e.currentTarget.value)}
      >
        <option value="all">All time</option>
        {availableMonths.map((m) => (
          <option key={m} value={m}>
            {formatMonthLabel(m)}
          </option>
        ))}
      </Select>
    </label>
  );
}

function ViewToggle({
  value,
  onChange,
}: {
  value: "cards" | "table";
  onChange: (v: "cards" | "table") => void;
}): React.JSX.Element {
  return (
    <div className="inline-flex rounded-md border border-input bg-background p-0.5 text-sm">
      <button
        type="button"
        onClick={() => onChange("cards")}
        className={cn(
          "px-3 py-1.5 rounded-sm font-medium transition-colors",
          value === "cards"
            ? "bg-accent text-accent-foreground"
            : "text-muted-foreground hover:text-foreground",
        )}
      >
        Cards
      </button>
      <button
        type="button"
        onClick={() => onChange("table")}
        className={cn(
          "px-3 py-1.5 rounded-sm font-medium transition-colors",
          value === "table"
            ? "bg-accent text-accent-foreground"
            : "text-muted-foreground hover:text-foreground",
        )}
      >
        Table
      </button>
    </div>
  );
}

function IndustrySummaryStrip({
  rows,
}: {
  rows: insights.IndustryRow[];
}): React.JSX.Element {
  return (
    <div className="flex flex-wrap gap-2">
      {rows.map((r) => (
        <span
          key={r.industry}
          className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs font-medium shadow-sm"
        >
          <span
            className="h-2 w-2 rounded-full"
            style={{ background: industryColor(r.industry) }}
          />
          {r.industry}
          <span className="text-muted-foreground">· {r.clientCount}</span>
        </span>
      ))}
    </div>
  );
}

function IndustryCards({
  rows,
}: {
  rows: insights.IndustryRow[];
}): React.JSX.Element {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {rows.map((row) => (
        <IndustryCard key={row.industry} row={row} />
      ))}
    </div>
  );
}

function IndustryCard({
  row,
}: {
  row: insights.IndustryRow;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const color = industryColor(row.industry);

  return (
    <section
      className="card-duress overflow-hidden"
      style={{ borderLeft: `4px solid ${color}` }}
    >
      <div className="flex items-center justify-between gap-3 px-5 pt-5">
        <div className="flex items-center gap-2">
          <span
            className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold"
            style={{ background: color, color: readableTextColor(color) }}
          >
            {row.industry}
          </span>
          <span className="text-xs text-muted-foreground">
            {row.clientCount} {row.clientCount === 1 ? "client" : "clients"}
          </span>
          {row.totalArr != null ? (
            <span className="text-xs font-medium text-foreground">
              {fmtARR(row.totalArr)} ARR
            </span>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 px-5 pt-4 sm:grid-cols-3">
        {COLUMNS.map((col) => {
          const stats = pickMetric(row.metrics, col.key);
          return (
            <div key={col.key}>
              <p className="section-label">{col.short}</p>
              <p className="num mt-0.5 text-lg font-semibold tabular-nums">
                {stats ? col.format(stats.avg) : "—"}
              </p>
            </div>
          );
        })}
        <div>
          <p className="section-label">Devices</p>
          <p className="num mt-0.5 text-lg font-semibold tabular-nums">
            {fmtInt(row.activeDeviceCount)}
          </p>
        </div>
      </div>

      <MoreStats row={row} />

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between border-t border-border px-5 py-3 text-xs font-medium text-muted-foreground hover:bg-muted/40 hover:text-foreground"
      >
        <span>
          {open ? "Hide" : "Show"} {row.clientCount}{" "}
          {row.clientCount === 1 ? "client" : "clients"}
        </span>
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </button>

      {open ? (
        <ul className="border-t border-border divide-y divide-border">
          {row.clients.map((c) => (
            <li key={c.clientId}>
              <Link
                to={`/clients/${c.slug}`}
                className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-3 px-5 py-3 text-sm hover:bg-muted/40"
              >
                <span className="truncate font-medium text-foreground">{c.clientName}</span>
                <span className="num text-xs tabular-nums text-muted-foreground">
                  Adopt {c.avgNetworkAdoption !== null ? fmtPct(c.avgNetworkAdoption) : "—"}
                </span>
                <span className="num text-xs tabular-nums text-muted-foreground">
                  Ext {c.avgExtendedRate !== null ? fmtPct(c.avgExtendedRate) : "—"}
                </span>
                <span className="num text-xs tabular-nums text-muted-foreground">
                  Sess {c.avgSessions !== null ? fmtInt(c.avgSessions) : "—"}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function MoreStats({ row }: { row: insights.IndustryRow }): React.JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-4 border-t border-border">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-5 py-3 text-xs font-medium text-muted-foreground hover:bg-muted/40 hover:text-foreground"
      >
        <span>{open ? "Hide" : "More"} stats</span>
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </button>
      {open ? (
        <div className="grid grid-cols-2 gap-3 border-t border-border px-5 py-4 sm:grid-cols-3">
          {MORE_STATS.map((col) => {
            const stats = col.accessor(row.metrics);
            return (
              <div key={col.key}>
                <p className="section-label">{col.short}</p>
                <p className="num mt-0.5 text-lg font-semibold tabular-nums">
                  {stats ? col.format(stats.avg) : "—"}
                </p>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function IndustryTable({
  rows,
}: {
  rows: insights.IndustryRow[];
}): React.JSX.Element {
  const { theme } = useTheme();
  const ranges = useMemo(() => {
    const out: Record<MetricKey, { min: number; max: number }> = {
      extended_rate: { min: Infinity, max: -Infinity },
      network_adoption: { min: Infinity, max: -Infinity },
      productive_hours: { min: Infinity, max: -Infinity },
      sessions: { min: Infinity, max: -Infinity },
      emergency_activations: { min: Infinity, max: -Infinity },
      // checkin_rate is unused here -- it's not in COLUMNS (see
      // insights-config.ts) -- but Record<MetricKey, ...> still requires an
      // entry for every MetricKey, since that type also backs peer
      // benchmarking elsewhere.
      checkin_rate: { min: Infinity, max: -Infinity },
    };
    for (const col of COLUMNS) {
      for (const row of rows) {
        const stats = pickMetric(row.metrics, col.key);
        if (!stats) continue;
        const r = out[col.key];
        if (stats.avg < r.min) r.min = stats.avg;
        if (stats.avg > r.max) r.max = stats.avg;
      }
    }
    return out;
  }, [rows]);

  return (
    <div className="card-duress overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-muted/40 text-left">
          <tr>
            <th className="px-4 py-3 font-medium">Industry</th>
            <th className="px-4 py-3 font-medium">Clients</th>
            <th className="px-4 py-3 font-medium">Devices</th>
            <th className="px-4 py-3 font-medium">Revenue</th>
            {COLUMNS.map((col) => (
              <th key={col.key} className="px-4 py-3 font-medium">
                <span className="flex flex-col">
                  <span>{col.short}</span>
                  <span className="text-[10px] font-normal text-muted-foreground">
                    {col.lowerIsBetter ? "lower better" : "higher better"}
                  </span>
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((row) => (
            <tr key={row.industry}>
              <td className="px-4 py-3">
                <span className="inline-flex items-center gap-2">
                  <span
                    className="h-2 w-2 rounded-full"
                    style={{ background: industryColor(row.industry) }}
                  />
                  <span className="font-medium">{row.industry}</span>
                </span>
              </td>
              <td className="px-4 py-3 tabular-nums">{row.clientCount}</td>
              <td className="px-4 py-3 tabular-nums">{fmtInt(row.activeDeviceCount)}</td>
              <td className="px-4 py-3 tabular-nums">
                {row.totalArr != null ? fmtARR(row.totalArr) : "—"}
              </td>
              {COLUMNS.map((col) => {
                const stats = pickMetric(row.metrics, col.key);
                const range = ranges[col.key];
                const colour = stats
                  ? heatmapColor(stats.avg, range.min, range.max, col.lowerIsBetter, theme === "dark")
                  : null;
                // Bold the best/worst cell per column so the extremes don't
                // rely on the colour wash alone (UI-13) -- the wash still
                // shows which end is which, this just makes them scannable
                // without needing to perceive colour at all.
                const best = col.lowerIsBetter ? range.min : range.max;
                const worst = col.lowerIsBetter ? range.max : range.min;
                const isExtreme = stats != null && (stats.avg === best || stats.avg === worst);
                return (
                  <td
                    key={col.key}
                    className="px-4 py-3"
                    style={colour ? { background: colour } : undefined}
                  >
                    {stats ? (
                      <Tooltip
                        content={`median ${col.format(stats.median)} · n=${stats.count}`}
                        side="top"
                      >
                        <span className={cn("num tabular-nums", isExtreme && "font-semibold")}>
                          {col.format(stats.avg)}
                        </span>
                      </Tooltip>
                    ) : (
                      <span className="num tabular-nums">—</span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <HeatmapLegend />
    </div>
  );
}

function HeatmapLegend(): React.JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-border px-4 py-2.5 text-xs text-muted-foreground">
      <span className="shrink-0 font-medium">Colour scale</span>
      <div className="flex items-center gap-2">
        <div
          className="h-2.5 w-20 rounded-sm"
          style={{
            background:
              "linear-gradient(to right, rgba(108,191,103,0.5), rgba(238,147,34,0.5), rgba(217,48,37,0.5))",
          }}
        />
        <span className="text-pill-green">Best</span>
        <span aria-hidden>→</span>
        <span className="text-pill-red">Worst</span>
      </div>
    </div>
  );
}

function heatmapColor(
  value: number,
  min: number,
  max: number,
  lowerIsBetter: boolean,
  isDark: boolean,
): string | null {
  // The same alpha reads clearly on a white card and barely at all on the
  // dark card (UI-13) -- raised for dark mode so the wash stays visible.
  // Starting estimate, not measured against a real screen.
  const alpha = isDark ? 0.38 : 0.18;
  if (!isFinite(min) || !isFinite(max) || max === min) {
    return isDark ? "rgba(76, 114, 184, 0.16)" : "rgba(76, 114, 184, 0.08)";
  }
  // Normalise so 0 = best, 1 = worst.
  const t = lowerIsBetter
    ? (value - min) / (max - min)
    : 1 - (value - min) / (max - min);
  // Green → amber → red. Same hue family as the rest of the app.
  const colours = [
    { stop: 0, rgb: [108, 191, 103] }, // falcon green
    { stop: 0.5, rgb: [238, 147, 34] }, // phoenix orange
    { stop: 1, rgb: [217, 48, 37] }, // emergency red
  ];
  let lower = colours[0];
  let upper = colours[colours.length - 1];
  for (let i = 0; i < colours.length - 1; i++) {
    const a = colours[i];
    const b = colours[i + 1];
    if (!a || !b) continue;
    if (t >= a.stop && t <= b.stop) {
      lower = a;
      upper = b;
      break;
    }
  }
  if (!lower || !upper) return null;
  const span = upper.stop - lower.stop || 1;
  const localT = (t - lower.stop) / span;
  const [lr, lg, lb] = lower.rgb;
  const [ur, ug, ub] = upper.rgb;
  if (lr === undefined || lg === undefined || lb === undefined) return null;
  if (ur === undefined || ug === undefined || ub === undefined) return null;
  const r = Math.round(lr + (ur - lr) * localT);
  const g = Math.round(lg + (ug - lg) * localT);
  const b = Math.round(lb + (ub - lb) * localT);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
